import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, Play as PlayIcon, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { resolvePlayers } from "@/lib/geometry";
import { randomSituation, sameJob, tolerance } from "@/lib/practice";
import { HIT_TEXT, JOB_OPTIONS, RUNNER_TEXT } from "@/lib/jobs";
import { PlayField } from "@/components/field/PlayField";
import { readyPositions } from "@/components/PlaybookView";
import { useReplay } from "@/hooks/useReplay";

const Marker = ({ pt, size, color, testId, cross }) => (
  <g transform={`translate(${pt[0]} ${-pt[1]})`} data-testid={testId} style={{ pointerEvents: "none" }}>
    {cross ? (
      <path d={`M${-size},${-size} L${size},${size} M${size},${-size} L${-size},${size}`} stroke={color} strokeWidth={size * 0.45} strokeLinecap="round" />
    ) : (
      <circle r={size} fill="none" stroke={color} strokeWidth={size * 0.3} className="runner-ring" />
    )}
  </g>
);

export const PracticeRound = ({ cfg, code, player, position, preset, drillsLeft, onResult, onSaved, onNext }) => {
  const sit = useMemo(() => preset || randomSituation(cfg), [cfg, preset]);
  const [play, setPlay] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [hit, setHit] = useState(null);
  const { primed, replay } = useReplay(setHit);

  useEffect(() => {
    api.joinPlays(code, sit).then((list) => setPlay(list.find((p) => p.fielder === sit.fielder) || null))
      .catch((e) => toast.error(formatApiError(e)));
  }, [code, sit]);

  const resolved = useMemo(() => play && resolvePlayers(play, sit.ball, cfg), [play, sit, cfg]);
  const target = resolved?.[position];

  const submit = useCallback((guess) => {
    if (answer || !target) return;
    const correct = guess.pt
      ? Math.hypot(guess.pt[0] - target.pt[0], guess.pt[1] - target.pt[1]) <= tolerance(cfg)
      : sameJob(guess.job, target.job);
    setAnswer({ ...guess, correct });
    setHit({ id: Date.now(), ball: sit.ball, hitType: sit.hitType, foul: false });
    onResult(correct);
    api.practiceAttempt(code, { player_id: player.player_id, position, key: play.key, correct_job: target.job, correct, method: guess.pt ? "tap" : "job" })
      .then(onSaved).catch((e) => toast.error(formatApiError(e)));
  }, [answer, target, cfg, sit, onResult, onSaved, code, player, position, play]);

  const revealed = !!answer && !primed;
  const positions = revealed ? resolved : readyPositions(cfg);
  const extras = (size) => (
    <>
      {!answer && <Marker pt={sit.ball} size={size * 0.9} color="#facc15" testId="practice-landing-marker" />}
      {revealed && answer.pt && <Marker pt={answer.pt} size={size * 0.6} color="#facc15" testId="practice-guess-marker" cross />}
    </>
  );

  return (
    <div className="grid gap-3 lg:gap-6 lg:grid-cols-[1.4fr_1fr] items-start">
      <div className="card space-y-1 lg:col-start-2 lg:row-start-1" data-testid="practice-prompt">
        <div className="flex items-center justify-between gap-2">
          <p className="ctl-label">You play {position}</p>
          {sit.drill && <span className="badge-coach" data-testid="practice-drill-badge">Coach's drill{drillsLeft > 1 ? ` · ${drillsLeft - 1} more` : ""}</span>}
        </div>
        <p className="font-heading text-2xl sm:text-3xl font-extrabold uppercase leading-tight" data-testid="practice-situation">
          {HIT_TEXT[sit.hitType]} · {sit.outs} out{sit.outs === 1 ? "" : "s"} · {RUNNER_TEXT[sit.runners]}
        </p>
        {sit.drill?.note && <p className="coach-callout !py-2" data-testid="practice-drill-note">{sit.drill.note}</p>}
        {!answer && <p className="font-semibold">The yellow ring is where the ball goes. <b>Where do you go?</b> Tap your spot or pick your job.</p>}
      </div>
      <div className="lg:col-start-1 lg:row-start-1 lg:row-span-2 field-frame">
        <PlayField cfg={cfg} runners={sit.runners} positions={positions} throws={play?.throws ?? []} runnerPaths={play?.runner_paths ?? []}
          hit={revealed ? hit : null} instant={primed} highlight={position} playId={`${play?.key}-${hit?.id}`}
          onTap={answer || !target ? undefined : (pt) => submit({ pt })} extras={extras} />
        {answer && (
          <div className="absolute top-2.5 right-2.5">
            <button data-testid="practice-replay-button" className="reset-btn" onClick={replay}><PlayIcon size={18} /> Replay</button>
          </div>
        )}
      </div>
      <div className="lg:col-start-2 lg:row-start-2 space-y-3">
        {!answer ? (
          <div className="card space-y-2" data-testid="practice-job-options">
            <span className="ctl-label">Or pick your job</span>
            <div className="grid grid-cols-2 gap-2">
              {JOB_OPTIONS.map((j) => (
                <button key={j.value} className="btn-ghost !text-base" data-testid={`practice-job-${j.value}`} disabled={!target} onClick={() => submit({ job: j.value })}>{j.label}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className={`card space-y-3 ${answer.correct ? "result-good" : "result-bad"}`} data-testid="practice-result">
            <div className="flex items-center gap-2">
              {answer.correct ? <CheckCircle2 size={28} /> : <XCircle size={28} />}
              <span className="font-heading text-3xl font-extrabold uppercase" data-testid="practice-result-text">{answer.correct ? "Nailed it!" : "Not quite"}</span>
            </div>
            <p className="font-bold" data-testid="practice-correct-job">Your job: {target.label}</p>
            {target.note && <p className="font-medium">Coach says: {target.note}</p>}
            <p className="text-sm font-medium">{play.explanation}</p>
            <button className="btn-primary w-full" data-testid="practice-next-button" onClick={onNext}>Next situation <ArrowRight size={18} /></button>
          </div>
        )}
      </div>
    </div>
  );
};
